<template>
  <div class="areas-list">
    <v-row align="center" class="pa-4 mb-2 areas-toolbar">
      <v-card-title class="areas-toolbar__title">Участки МТО</v-card-title>

      <div class="areas-toolbar__search">
        <BaseTextField
          v-model="filterName"
          type-input="string"
          :is-button="false"
          label="Название"
        />
      </div>

      <v-autocomplete
        v-model="filterServiceAreaId"
        placeholder="Район обслуживания"
        :items="serviceAreas"
        item-text="name"
        item-value="id"
        prepend-inner-icon="mdi-map-marker-multiple"
        outlined
        dense
        hide-details
        clearable
        class="areas-toolbar__filter"
      />

      <v-autocomplete
        v-model="filterAgentId"
        placeholder="Агент"
        :items="agents"
        item-text="user.name"
        item-value="id"
        prepend-inner-icon="mdi-account-tie-outline"
        outlined
        dense
        hide-details
        clearable
        class="areas-toolbar__filter"
      />

      <v-spacer />

      <div class="areas-toolbar__actions">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="#4C6C93"
              class="border card-footer"
              v-bind="attrs"
              v-on="on"
              :loading="loading"
              @click="loadData"
            >
              <v-icon style="font-size: 40px; height: inherit;" color="white">mdi-reload</v-icon>
            </v-btn>
          </template>
          <span>Обновить</span>
        </v-tooltip>

        <v-tooltip v-if="$checkPermission('store_mto_sector')" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="green"
              class="border card-footer card-footer--create"
              v-bind="attrs"
              v-on="on"
              @click="openCreate"
            >
              <v-icon style="font-size: 40px; height: inherit;" color="white">mdi-new-box</v-icon>
            </v-btn>
          </template>
          <span>Новый участок МТО</span>
        </v-tooltip>
      </div>
    </v-row>

    <v-data-table
      class="table"
      :headers="headers"
      :items="displayItems"
      :loading="loading"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :footer-props="footerProps"
      item-key="id"
    >
      <template v-slot:item.service_area_name="{ item }">
        {{ getServiceAreaName(item.service_area_id) }}
      </template>

      <template v-slot:item.masters="{ item }">
        <span v-if="item.master && item.master.name">{{ item.master.name }}</span>
        <span v-else-if="item.masters && item.masters.length">
          {{ item.masters.map(m => m.name).join(', ') }}
        </span>
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.equipment_counts="{ item }">
        {{ formatEquipmentCount(item.equipment_counts) }}
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="areas-table-actions" @click.stop>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                text
                tile
                small
                v-bind="attrs"
                v-on="on"
                class="btn__icon pa-0 mr-1"
                width="18"
                height="18"
                :to="`/mto-sector/${item.id}`"
              >
                <v-icon size="large">mdi-eye-outline</v-icon>
              </v-btn>
            </template>
            <span>Открыть участок</span>
          </v-tooltip>

          <v-tooltip v-if="$checkPermission('update_mto_sector')" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                text
                tile
                small
                v-bind="attrs"
                v-on="on"
                class="btn__icon pa-0 mr-1"
                width="18"
                height="18"
                @click="openEdit(item)"
              >
                <v-icon size="large">mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span>Редактировать</span>
          </v-tooltip>

          <v-tooltip v-if="$checkPermission('destroy_mto_sector')" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                text
                tile
                small
                v-bind="attrs"
                v-on="on"
                class="btn__icon pa-0"
                width="18"
                height="18"
                @click="confirmDelete(item)"
              >
                <v-icon size="large">mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Удалить</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <mto-sector-form-dialog
      v-model="formDialog"
      :item="editItem"
      :service-areas="serviceAreas"
      @saved="loadData"
    />

    <v-dialog v-model="deleteDialog" max-width="400" content-class="dialog-border-radius">
      <v-card>
        <v-card-title>Удалить участок МТО?</v-card-title>
        <v-card-text v-if="deleteItem">
          «{{ deleteItem.name }}» будет удалён безвозвратно.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Отмена</v-btn>
          <v-btn color="error" text @click="doDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BaseTextField from '@/components/BaseTextField';
import MtoSectorFormDialog from '@/components/areas/MtoSectorFormDialog';

export default {
  name: 'MtoSectorsList',
  components: { BaseTextField, MtoSectorFormDialog },
  data () {
    return {
      loading: false,
      formDialog: false,
      editItem: null,
      deleteDialog: false,
      deleteItem: null,
      filterName: '',
      filterServiceAreaId: null,
      filterAgentId: null,
      page: 1,
      itemsPerPage: 20,
      footerProps: {
        showFirstLastPage: true,
        'items-per-page-options': [10, 20, 50, 100],
        'items-per-page-text': 'Строк на странице',
      },
      headers: [
        { text: 'Название', value: 'name' },
        { text: 'Район обслуживания', value: 'service_area_name', sortable: false },
        { text: 'Мастер', value: 'masters', sortable: false },
        { text: 'Показатели', value: 'equipment_counts', sortable: false },
        { text: '', value: 'actions', width: '12%', sortable: false, align: 'center' },
      ],
    };
  },
  created () {
    this.loadOptions();
    this.loadData();
  },
  watch: {
    filterName () {
      this.page = 1;
    },
    filterServiceAreaId () {
      this.page = 1;
      this.loadData();
    },
    filterAgentId () {
      this.page = 1;
      this.loadData();
    },
  },
  computed: {
    ...mapGetters({
      mtoSectors: 'MtoSector/mtoSectors',
      serviceAreas: 'ServiceArea/serviceAreas',
      agents: 'Agents/agents',
    }),
    displayItems () {
      return this.mtoSectors.filter((item) => {
        const nameMatch = !this.filterName || item.name.toLowerCase().includes(this.filterName.toLowerCase());
        return nameMatch;
      });
    },
  },
  methods: {
    ...mapActions({
      getMtoSectors: 'MtoSector/getMtoSectors',
      getServiceAreas: 'ServiceArea/getServiceAreas',
      loadAllAgents: 'Agents/loadAllAgents',
      deleteMtoSector: 'MtoSector/deleteMtoSector',
    }),
    async loadOptions () {
      await Promise.all([
        this.getServiceAreas(),
        this.loadAllAgents(),
      ]);
    },
    async loadData () {
      this.loading = true;
      const options = {};
      if (this.filterServiceAreaId) options.service_area_id = this.filterServiceAreaId;
      if (this.filterAgentId) options.agent_id = this.filterAgentId;
      try {
        await this.getMtoSectors(options);
      } catch (e) {
        this.$flash_warning('Не удалось загрузить участки МТО');
      } finally {
        this.loading = false;
      }
    },
    formatEquipmentCount (counts) {
      if (!counts) return '—';
      const parts = [
        counts.doorphones ? `Дом. ${counts.doorphones}` : null,
        counts.apartments ? `Кв. ${counts.apartments}` : null,
        counts.apartments_with_phone_setup ? `Возм. трубки ${counts.apartments_with_phone_setup}` : null,
      ].filter(Boolean);
      return parts.join(' · ') || String(counts.total ?? 0);
    },
    getServiceAreaName (id) {
      const area = this.serviceAreas.find(a => a.id === id);
      return area ? area.name : '—';
    },
    openCreate () {
      this.editItem = null;
      this.formDialog = true;
    },
    openEdit (item) {
      this.editItem = { ...item };
      this.formDialog = true;
    },
    confirmDelete (item) {
      this.deleteItem = item;
      this.deleteDialog = true;
    },
    async doDelete () {
      if (!this.deleteItem) return;
      try {
        await this.deleteMtoSector(this.deleteItem.id);
        this.$flash_success('Участок МТО удалён');
        this.deleteDialog = false;
        this.deleteItem = null;
        await this.loadData();
      } catch (e) {
        this.$flash_warning('Не удалось удалить участок МТО');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './areasList.scss';
</style>
